import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Rocket, List, Server, Play, Square, Clock } from 'lucide-react';
import timeAgo from '../utils/timeAgo';
import './DashboardHome.css';

const DashboardHome = () => {
  const navigate = useNavigate();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const backend_url = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserName(decoded.name || '');
      } catch (e) {
        console.error('Failed to decode token', e);
      }
    }

    const fetchClusters = async () => {
      try {
        const res = await fetch(`${backend_url}/api/clusters`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setClusters(res.ok && Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching clusters:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClusters();
  }, [backend_url]);

  const stats = {
    total: clusters.length,
    running: clusters.filter(c => c.state === 'Running').length,
    stopped: clusters.filter(c => c.state === 'Stopped').length,
    pending: clusters.filter(c => c.state === 'Pending' || c.progress_status === 'in-progress').length,
  };

  const recentClusters = clusters.slice(0, 5);

  const statCards = [
    { label: 'Total Clusters', value: stats.total, icon: Server, accent: '#3b82f6' },
    { label: 'Running', value: stats.running, icon: Play, accent: '#10b981' },
    { label: 'Stopped', value: stats.stopped, icon: Square, accent: '#f59e0b' },
    { label: 'In Progress', value: stats.pending, icon: Clock, accent: '#8b5cf6' },
  ];

  const getProgressTag = (cluster) => {
    if (cluster.progress_status === 'complete' && cluster.state === 'Running') {
      return <span className="progress-tag complete">Complete</span>;
    }
    if (cluster.progress_status === 'in-progress') {
      return <span className="progress-tag in-progress">Stage {cluster.stage}</span>;
    }
    if (cluster.progress_status === 'incomplete') {
      return <span className="progress-tag incomplete">Stage {cluster.stage} - Failed</span>;
    }
    return <span className="progress-tag pending">Stage {cluster.stage || 1}</span>;
  };

  return (
    <div className="dashboard-home">
      <div className="greeting">
        <h1>Welcome back{userName ? `, ${userName}` : ''}</h1>
        <p>Here's an overview of your cluster infrastructure</p>
      </div>

      <div className="stats-grid">
        {statCards.map((card) => (
          <div className="stat-card" key={card.label} style={{ borderTopColor: card.accent }}>
            <div className="stat-icon-wrap" style={{ backgroundColor: `${card.accent}10` }}>
              <card.icon size={20} style={{ color: card.accent }} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{loading ? '—' : card.value}</span>
              <span className="stat-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="quick-actions-section">
        <div className="home-section-header">Quick Actions</div>
        <div className="quick-actions">
          <button className="action-btn primary" onClick={() => navigate('/dashboard/deploy')}>
            <Rocket size={18} />
            Launch Cluster
          </button>
          <button className="action-btn secondary" onClick={() => navigate('/dashboard/list')}>
            <List size={18} />
            Cluster Overview
          </button>
        </div>
      </div>

      <div className="recent-section">
        <div className="home-section-header">Recent Clusters</div>
        {loading ? (
          <div className="recent-loading">Loading...</div>
        ) : recentClusters.length === 0 ? (
          <div className="recent-empty">
            No clusters yet. Launch your first cluster to get started.
          </div>
        ) : (
          <table className="recent-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>State</th>
                <th>Progress</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {recentClusters.map((cluster) => (
                <tr key={cluster.id} onClick={() => navigate('/dashboard/list')}>
                  <td className="recent-name">{cluster.name}</td>
                  <td className="recent-domain">{cluster.domain || '\u2014'}</td>
                  <td>
                    <span className={`status-tag ${cluster.state.toLowerCase().replace(/\s+/g, '-')}`}>
                      {cluster.state}
                    </span>
                  </td>
                  <td>{getProgressTag(cluster)}</td>
                  <td className="recent-time">{timeAgo(cluster.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
